import React, { useState, ChangeEvent } from 'react'
import { InputAdornment, IconButton } from '@mui/material'
import TextField from '@mui/material/TextField'
import { Field } from 'formik'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEyeSlash, faEye } from '@fortawesome/free-solid-svg-icons'
import { styled } from '@mui/system'

interface InputProps {
  disabled?: boolean
  label?: string
  id: string
  placeholder?: string
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  value: string
  name?: string
  type?: string
  error?: string
  touched?: boolean
  className?: string
  variant?: boolean
  labelInput?: string
  classNameLabel?: string
  classNameInput?: string
  required?: boolean
  setFieldValue?: string
  children?: React.ReactNode
  justify?: string
  onClick?: () => void
  customClass?: string
  bottomSpace?: boolean
}

const InputContainer = styled('div')({
  // Your container styles here
})

const Label = styled('p')({
  // Your label styles here
})

const InputField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    fontSize: 14,
    backgroundColor: '#e8e8e8',
    fontFamily: 'Poppins',
    borderRadius: '10px',
    color: 'black',
  },
  '& .MuiOutlinedInput-inputMarginDense': {
    paddingTop: '16px',
    paddingBottom: '10px',
  },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
    borderBottomColor: 'transparent',
  },
  '& .MuiTextField-root': {
    color: 'transparent',
  },
  '& .MuiInput-underline:before': {
    borderBottomColor: 'transparent',
  },
  '& label.Mui-focused': {
    color: 'transparent',
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: 'transparent',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'transparent',
    },
    '&:hover fieldset': {
      borderColor: 'transparent',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'transparent',
    },
  },
  '& .MuiFormLabel-root': {
    zIndex: 0,
  },
  '& .MuiFormHelperText-root.Mui-error': {
    fontSize: 'small',
    marginLeft: 4,
    position: 'absolute',
    bottom: -20,
  },
}))

const Input = ({
  disabled = false,
  placeholder,
  touched,
  label,
  className = '',
  error,
  variant = true,
  setFieldValue,
  labelInput,
  value = '',
  classNameLabel = 'label',
  classNameInput = 'input',
  type = 'text',
  justify = 'justify-start',
  children,
  onClick,
  customClass,
  bottomSpace,
  ...inputProps
}: InputProps) => {
  const [showPassword, setShowPassword] = useState(false)

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => event.preventDefault()

  const { t } = useTranslation()

  type Variant = 'filled' | 'standard' | 'outlined'
  let variantValue: Variant = 'outlined'

  return (
    <Field name={inputProps.id}>
      {() => {
        let variantText: Variant | undefined = variantValue

        return (
          <InputContainer className={`input-container ${justify} ${className}`}>
            {label !== undefined && label !== '' && (
              <Label className={`${classNameLabel} label-size`}>{label}</Label>
            )}
            <div className={`${classNameInput}`}>
              <InputField
                className={customClass}
                disabled={disabled}
                fullWidth={true}
                placeholder={placeholder}
                label={value ? '' : labelInput}
                id={`bootstrap-input-${inputProps.id}`}
                error={!!error}
                helperText={error}
                variant={variantText}
                size='small'
                InputLabelProps={{
                  shrink: false,
                }}
                type={
                  type === 'password'
                    ? showPassword
                      ? 'text'
                      : 'password'
                    : type
                }
                value={value || value.length >= 0 ? value : ''}
                InputProps={{
                  endAdornment: type === 'password' && (
                    <InputAdornment position='end'>
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={handleMouseDownPassword}
                      >
                        {showPassword ? (
                          <FontAwesomeIcon
                            className='icon-option-extra'
                            icon={faEye}
                          />
                        ) : (
                          <FontAwesomeIcon
                            className='icon-option-extra'
                            icon={faEyeSlash}
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                  ...inputProps,
                }}
              />
            </div>
            {children}
          </InputContainer>
        )
      }}
    </Field>
  )
}

export default Input
